import React, {useRef} from 'react';
import clsx from 'clsx';
import {Button, Typography} from '@mui/material';
import {Alert} from '@netx/core-components/Alert';
import {LegacyFormTextField} from '@netx/core-components/Form/TextField';
import {LegacyFormPasswordField} from '@netx/core-components/Form/PasswordField';
import useForm from '@netx/core-components/Form/hooks/useForm';
import {
	LegacyForm as Form,
	type LegacyFormProps as FormProps,
} from '@netx/core-components/Form/LegacyForm';
import {getInputFormObject} from '@netx/core-components/Form/util';
import {validateEmail} from '@netx/core-components/Form/validators';
import {
	ScrollBorder,
	scrollBorderClasses,
} from '@netx/core-components/ScrollBorder';
import {SubmitButton} from '@netx/core-components/SubmitButton';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {LegacyFormCustomForm} from '@netx/core-custom-form/components/CustomForm';
import {AuthFieldLabel, AuthTitle, AuthActionLabel} from '../../locales';
import {RegisterFormData} from '../../types';
import {useSelfRegistration} from '../../hooks';
import {
	registerClasses,
	getRegisterUtilityClass,
	RegisterClasses,
	REGISTER_CLASS_NAME,
} from './registerClasses';
import {RegisterAwaitingApproval} from './RegisterAwaitingApproval';
import {authRequireEmailAsLogin} from '@netx/core-auth-next/enablers';

export type RegisterOwnerState = WithClasses<RegisterClasses, {}>;

const useUtilityClasses = (ownerState: RegisterOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getRegisterUtilityClass, classes);
};

const RegisterRoot = styled('div', {
	name: REGISTER_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	...theme.mixins.flexFillColumn,
	alignItems: 'center',
	'.input-field + .input-field': {
		marginTop: theme.spacing(2),
	},
}));

const RegisterHeader = styled('header')(({theme}) => ({
	flex: '0 0 auto',
	padding: '24px 16px',
}));

const RegisterContent = styled(ScrollBorder)(({theme}) => ({
	[`& .${scrollBorderClasses.scrollWrapper}`]: {
		padding: theme.spacing(1, 6, 3, 6),
	},
}));

const RegisterFooter = styled('footer')(({theme}) => ({
	flex: '0 0 auto',
	alignItems: 'center',
	display: 'flex',
	width: '100%',
	justifyContent: 'space-between',
	padding: '24px',
}));

const RegisterForm = styled(Form)(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	width: '100%',
})) as (props: FormProps<RegisterFormData>) => JSX.Element;

export type RegisterProps = WithClasses<
	RegisterClasses,
	{
		onCancel?: () => void;
	}
>;

export const RegisterPrimitive = ({
	classes: _classes,
	className,
	onCancel,
	...props
}: RegisterProps) => {
	const {awaitingApproval, customForm, registering, selfRegisterUser} =
		useSelfRegistration();
	const ref = useRef<HTMLDivElement>(null);
	const emailAsLogin = authRequireEmailAsLogin();

	const form = useForm<RegisterFormData>(undefined, {
		onSubmit: selfRegisterUser,
	});
	const inputForm = getInputFormObject(form);

	const ownerState: RegisterOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<RegisterRoot className={clsx(classes.root, className)} ref={ref}>
			<RegisterHeader>
				<Typography variant="h5">{AuthTitle.REGISTER_ACCOUNT}</Typography>
			</RegisterHeader>

			{awaitingApproval ? (
				<RegisterAwaitingApproval onCancel={onCancel} />
			) : (
				<RegisterForm form={form}>
					<RegisterContent>
						<LegacyFormTextField
							className={clsx('input-field')}
							disabled={form.isSubmitting}
							error={form.errors.name1}
							form={inputForm}
							name="name1"
							label={AuthFieldLabel.name1}
							value={form.values.name1}
							required
						/>

						<LegacyFormTextField
							className={clsx('input-field')}
							disabled={form.isSubmitting}
							error={form.errors.name3}
							form={inputForm}
							name="name3"
							label={AuthFieldLabel.name3}
							value={form.values.name3}
							required
						/>

						<LegacyFormTextField
							className={clsx('input-field')}
							disabled={form.isSubmitting}
							error={form.errors.email}
							form={inputForm}
							name="email"
							label={AuthFieldLabel.email}
							required={emailAsLogin}
							helperText={emailAsLogin ? AuthFieldLabel.login : undefined}
							value={form.values.email}
							formFieldOptions={{
								validator: validateEmail,
							}}
						/>

						{emailAsLogin ? null : (
							<LegacyFormTextField
								className={clsx('input-field')}
								disabled={form.isSubmitting}
								error={form.errors.login}
								form={inputForm}
								name="login"
								label={AuthFieldLabel.login}
								required
								value={form.values.login}
							/>
						)}

						<LegacyFormPasswordField
							className={clsx('input-field')}
							disabled={form.isSubmitting}
							isNewPassword
							error={form.errors.password1}
							form={inputForm}
							required
							value={form.values.password1}
						/>

						<LegacyFormPasswordField
							className={clsx('input-field')}
							disabled={form.isSubmitting}
							isConfirm
							error={form.errors.password2}
							form={inputForm}
							required
							value={form.values.password2}
						/>

						<LegacyFormCustomForm<RegisterFormData>
							disabled={form.isSubmitting}
							form={form}
							inputForm={inputForm}
							items={customForm?.data?.formData || []}
						/>
					</RegisterContent>

					<RegisterFooter>
						<Button
							disabled={form.isSubmitting}
							variant="text"
							onClick={() => {
								onCancel?.();
							}}
						>
							{AuthActionLabel.RETURN_TO_LOGIN}
						</Button>
						<SubmitButton
							showWorking={form.isSubmitting}
							disabled={!form.isValid || !form.hasChanged}
							label={AuthActionLabel.CREATE_ACCOUNT}
						/>
					</RegisterFooter>
				</RegisterForm>
			)}
		</RegisterRoot>
	);
};

export const Register = RegisterPrimitive;

export default Register;
